import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { usePublishLegalDocument } from "@/hooks/useLegalDocuments";

/** Today in the BUSINESS timezone — the day the server's `effective_at` is read against. */
function todayEt(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(new Date());
}

/** Mirrored from the API's dto/legal so the client refuses first and the server refuses last. */
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = "application/pdf";

const fmtSize = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} KB`;

/**
 * "Upload new version" — publish a version of the Platform Terms.
 *
 * Deliberately three fields. Everything else about the published row is derived: the version string
 * comes from the effective date, the storage key from the upload instant, the hash from the bytes.
 * An operator typing a version number is an operator who can mislabel one.
 *
 * The dialog states the two consequences that are not visible on the form — when the version starts
 * being served, and that existing clients are not re-prompted by this upload — because both are
 * decisions being made here whether or not anyone realises it.
 */
export default function UploadTermsDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const publish = usePublishLegalDocument();
  const [file, setFile] = useState<File | null>(null);
  const [effectiveFrom, setEffectiveFrom] = useState(todayEt());
  const [isMaterial, setIsMaterial] = useState(false);
  // Two errors, because they are about different things and belong in different places: a rejected
  // FILE is rejected at the drop zone, while a failed publish is about the request and belongs by
  // the button that sent it. One state variable meant a server error appearing under the file.
  const [fileError, setFileError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // A COUNTER, not a boolean: dragenter/dragleave fire again for every child element the cursor
  // crosses, so a boolean turns the highlight off as soon as the pointer reaches the text inside
  // the zone. Counting enter and leave means only the outermost leave clears it.
  const [dragDepth, setDragDepth] = useState(0);
  const dragging = dragDepth > 0;

  function reset() {
    setFile(null);
    setEffectiveFrom(todayEt());
    setIsMaterial(false);
    setFileError(null);
    setSubmitError(null);
    setDragDepth(0);
  }

  function handleClose() {
    if (publish.isPending) return; // a publish in flight has already written the object
    reset();
    onClose();
  }

  function handleFile(picked: File | null) {
    setFileError(null);
    if (!picked) return setFile(null);
    if (picked.type !== ACCEPT) return setFileError("The document must be a PDF.");
    if (picked.size === 0) return setFileError("That file is empty.");
    if (picked.size > MAX_BYTES) return setFileError("The document must be 10MB or less.");
    setFile(picked);
  }

  async function handlePublish() {
    if (!file) return;
    setSubmitError(null);
    try {
      await publish.mutateAsync({ file, docKey: "platform_terms", effectiveFrom, isMaterial });
      reset();
      onClose();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : "Couldn't publish the document.");
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontFamily: "var(--font-crimson)", fontSize: 21 }}>
        Upload a new version
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: "var(--color-graphite)", mb: 2 }}>
          The file is stored as published and cannot be replaced afterwards.
        </Typography>

        <Alert severity="info" sx={{ mb: 2.5, fontSize: 12.5 }}>
          Signup serves this version from its effective date. Organizations that already accepted an
          earlier version are not asked again — re-acceptance has yet to be designed and built.
        </Alert>

        {/* Drop and pick land on the same `handleFile`, so a dragged file gets the identical type
            and size checks as a browsed one — the drop is another way in, not another path.
            `preventDefault` on dragOver is what stops the browser navigating away to render the
            PDF, which is its default for a dropped file and looks like the dialog vanishing. */}
        <Box
          onDragEnter={(e) => {
            e.preventDefault();
            setDragDepth((d) => d + 1);
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={() => setDragDepth((d) => Math.max(0, d - 1))}
          onDrop={(e) => {
            e.preventDefault();
            setDragDepth(0);
            handleFile(e.dataTransfer.files?.[0] ?? null);
          }}
          sx={{
            border: `1.5px dashed ${dragging ? "var(--color-dark-dust)" : "var(--color-rodeo-dust)"}`,
            borderRadius: "10px",
            bgcolor: dragging ? "var(--color-light-dust)" : "var(--color-clear-dust)",
            p: 2.5,
            textAlign: "center",
            mb: fileError ? 1.5 : 2.5,
            transition: "background-color 0.12s, border-color 0.12s",
          }}
        >
          {file ? (
            <>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{file.name}</Typography>
              <Typography sx={{ fontSize: 12, color: "var(--color-graphite)", mt: 0.4 }}>
                PDF · {fmtSize(file.size)}
              </Typography>
            </>
          ) : (
            <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
              {dragging ? "Drop to attach" : "Drag a PDF here — 10MB or less"}
            </Typography>
          )}
          <Button component="label" size="small" sx={{ mt: 1 }}>
            {file ? "Choose a different file" : "or choose a file"}
            <input
              type="file"
              accept="application/pdf"
              hidden
              // Cleared after every pick so choosing the SAME file twice still fires onChange —
              // without it, re-picking a file you just replaced does nothing at all.
              onChange={(e) => {
                handleFile(e.target.files?.[0] ?? null);
                e.target.value = "";
              }}
            />
          </Button>
        </Box>

        {/* Directly under the drop zone, not at the foot of the dialog: a rejected file is rejected
            HERE, and an error below the date and the checkbox reads as being about them. */}
        {fileError && (
          <Alert severity="error" sx={{ mb: 2.5, fontSize: 12.5 }}>
            {fileError}
          </Alert>
        )}

        <TextField
          label="Effective from"
          type="date"
          fullWidth
          size="small"
          value={effectiveFrom}
          onChange={(e) => setEffectiveFrom(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          helperText="A future date publishes the version now and serves it from that day."
        />

        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 1.5 }}>
          <Checkbox
            checked={isMaterial}
            onChange={(e) => setIsMaterial(e.target.checked)}
            size="small"
            sx={{ p: 0.5, mt: -0.25 }}
          />
          {/* Records the judgment, and says so. An earlier draft of this line promised existing
              clients would be re-prompted at next sign-in, which nothing in the product does — the
              flag is stored and read by no one until re-acceptance is built. Copy that describes
              behaviour the build does not have is worse than no copy: it is relied on. */}
          <Typography sx={{ fontSize: 13, lineHeight: 1.45 }}>
            <strong>Material change.</strong> Records this version as one existing organizations
            should have to accept again.
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize: 12,
            lineHeight: 1.5,
            color: "var(--color-good-ochre)",
            bgcolor: "var(--color-good-ochre-bg)",
            border: "1px solid var(--color-good-ochre-border)",
            borderRadius: "8px",
            p: 1.25,
            mt: 1.5,
          }}
        >
          <strong>Important.</strong> The feature that prompts existing organizations to re-accept a
          material change has yet to be designed and built. Marking this flag records the decision;
          it does not notify anyone today.
        </Typography>
        {submitError && (
          <Alert severity="error" sx={{ mt: 2, fontSize: 12.5 }}>
            {submitError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={publish.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handlePublish}
          disabled={!file || !effectiveFrom || publish.isPending}
          sx={{ bgcolor: "var(--color-rodeo-black)" }}
        >
          {publish.isPending ? "Publishing…" : "Publish version"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
